/**
 * DeliveryManagement - Handles delivery tracking and management
 * Supports both bulk and partial deliveries with history tracking
 */

import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
  Tooltip,
  Alert,
  LinearProgress,
  Divider
} from '@mui/material';
import {
  LocalShipping as DeliveryIcon,
  Add as AddIcon,
  History as HistoryIcon,
  CheckCircle as CompleteIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { UnifiedOrder, DeliveryLog, OrderPosition } from '../../types/unifiedOrderTypes';
import { unifiedOrderService } from '../../services/unifiedOrderService';
import { useNotification } from '../../contexts/NotificationContext';
import { formatDate, formatDateForInput } from '../../utils/dateUtils';

interface DeliveryManagementProps {
  order: UnifiedOrder;
  deliveryHistory: DeliveryLog[];
  onDeliveryUpdated: () => void;
}

export default function DeliveryManagement({ 
  order, 
  deliveryHistory, 
  onDeliveryUpdated 
}: DeliveryManagementProps) {
  const [partialDeliveryOpen, setPartialDeliveryOpen] = useState(false);
  const [bulkDeliveryOpen, setBulkDeliveryOpen] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState<OrderPosition | null>(null);
  const [deliveryForm, setDeliveryForm] = useState({
    delivered_quantity: '',
    delivery_date: formatDateForInput(new Date()),
    received_by: '',
    delivery_notes: ''
  });
  const [loading, setLoading] = useState(false);
  const { showNotification } = useNotification();

  const handlePartialDelivery = async () => {
    if (!selectedPosition) return;

    const deliveredQty = parseInt(deliveryForm.delivered_quantity);
    if (isNaN(deliveredQty) || deliveredQty <= 0) {
      showNotification('Bitte geben Sie eine gültige Menge ein', 'error'); /* Please enter a valid quantity */
      return;
    }

    if (deliveredQty > selectedPosition.open_quantity) {
      showNotification(`Kann nicht mehr als ${selectedPosition.open_quantity} verbleibende Einheiten liefern`, 'error'); /* Cannot deliver more than ${selectedPosition.open_quantity} remaining units */
      return;
    }

    try {
      setLoading(true);
      const response = await unifiedOrderService.recordPartialDelivery(
        order.id,
        selectedPosition.id,
        {
          delivered_quantity: deliveredQty,
          delivery_date: deliveryForm.delivery_date,
          received_by: deliveryForm.received_by,
          delivery_notes: deliveryForm.delivery_notes
        }
      );

      if (response.success) {
        showNotification(`${deliveredQty} Einheiten erfolgreich geliefert`, 'success'); /* Successfully delivered ${deliveredQty} units */
        setPartialDeliveryOpen(false);
        resetForm();
        onDeliveryUpdated();
      }
    } catch (error: any) {
      showNotification(`Fehler beim Erfassen der Lieferung: ${error.message}`, 'error'); /* Error recording delivery: */
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDelivery = async () => {
    try {
      setLoading(true);
      const response = await unifiedOrderService.recordBulkDelivery(
        order.id,
        {
          delivery_date: deliveryForm.delivery_date,
          received_by: deliveryForm.received_by,
          delivery_notes: deliveryForm.delivery_notes
        }
      );

      if (response.success) {
        showNotification('Gesamtlieferung erfolgreich abgeschlossen', 'success'); /* Bulk delivery completed successfully */
        setBulkDeliveryOpen(false);
        resetForm();
        onDeliveryUpdated();
      }
    } catch (error: any) {
      showNotification(`Fehler beim Erfassen der Gesamtlieferung: ${error.message}`, 'error'); /* Error recording bulk delivery: */
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setDeliveryForm({
      delivered_quantity: '',
      delivery_date: formatDateForInput(new Date()),
      received_by: '',
      delivery_notes: ''
    });
    setSelectedPosition(null);
  };

  const openPartialDelivery = (position: OrderPosition) => {
    setSelectedPosition(position);
    setDeliveryForm(prev => ({
      ...prev,
      delivered_quantity: position.open_quantity.toString()
    }));
    setPartialDeliveryOpen(true);
  };

  const getDeliveryStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'partial': return 'info';
      case 'delivered': return 'success';
      default: return 'default';
    }
  };

  const hasOpenPositions = order.positions.some(pos => pos.open_quantity > 0);

  return (
    <Box>
      {/* Delivery Actions */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Lieferaktionen {/* Delivery Actions */}
          </Typography>
          
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <Button
                variant="contained"
                startIcon={<DeliveryIcon />}
                onClick={() => setBulkDeliveryOpen(true)}
                disabled={!hasOpenPositions}
                color="primary"
              >
                Alle Lieferungen abschließen {/* Complete All Deliveries */}
              </Button>
            </Grid>
            <Grid item>
              <Typography variant="body2" color="text.secondary">
                Alle verbleibenden Mengen für alle Positionen liefern {/* Deliver all remaining quantities for all positions */}
              </Typography>
            </Grid>
          </Grid>

          {!hasOpenPositions && (
            <Alert severity="success" sx={{ mt: 2 }}>
              Alle Positionen wurden vollständig geliefert! {/* All positions have been fully delivered! */}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Positions Overview */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Positions-Lieferstatus {/* Position Delivery Status */}
          </Typography>
          
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Pos #</TableCell>
                  <TableCell>Teil</TableCell> {/* Part */}
                  <TableCell align="right">Bestellt</TableCell> {/* Ordered */}
                  <TableCell align="right">Geliefert</TableCell> {/* Delivered */}
                  <TableCell align="right">Verbleibt</TableCell> {/* Remaining */}
                  <TableCell>Fortschritt</TableCell> {/* Progress */}
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Aktionen</TableCell> {/* Actions */}
                </TableRow>
              </TableHead>
              <TableBody>
                {order.positions.map((position) => {
                  const progressPercent = (position.delivered_quantity / position.quantity) * 100;
                  
                  return (
                    <TableRow key={position.id}>
                      <TableCell>{position.position_number}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {position.part_name || 'Kein Name'} {/* No name */}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {position.part_number || 'Keine Teilnummer'} {/* No part number */}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="medium">
                          {position.quantity}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="success.main">
                          {position.delivered_quantity}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="warning.main">
                          {position.open_quantity}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={progressPercent}
                            sx={{ 
                              width: 80, 
                              height: 8, 
                              borderRadius: 4,
                              backgroundColor: 'rgba(0,0,0,0.1)'
                            }}
                            color={progressPercent === 100 ? 'success' : 'primary'}
                          />
                          <Typography variant="caption">
                            {Math.round(progressPercent)}%
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={position.delivery_status}
                          color={getDeliveryStatusColor(position.delivery_status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        {position.open_quantity > 0 && (
                          <Tooltip title="Teillieferung erfassen" /* Record partial delivery */>
                            <IconButton
                              size="small"
                              onClick={() => openPartialDelivery(position)}
                              color="primary"
                            >
                              <AddIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Delivery History */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <HistoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Lieferverlauf {/* Delivery History */}
          </Typography>
          
          {deliveryHistory.length === 0 ? (
            <Alert severity="info">
              Noch keine Lieferungen erfasst {/* No deliveries recorded yet */}
            </Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Datum</TableCell> {/* Date */}
                    <TableCell>Position</TableCell>
                    <TableCell align="right">Menge</TableCell> {/* Quantity */}
                    <TableCell>Empfangen von</TableCell> {/* Received By */}
                    <TableCell>Anmerkungen</TableCell> {/* Notes */}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deliveryHistory.map((delivery) => (
                    <TableRow key={delivery.id}>
                      <TableCell>
                        {formatDate(new Date(delivery.delivery_date))}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          Pos #{delivery.position_number}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {delivery.part_name}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="medium">
                          {delivery.delivered_quantity}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {delivery.created_by || 'Nicht angegeben'} {/* Not specified */}
                      </TableCell>
                      <TableCell>
                        {delivery.notes || 'Keine Anmerkungen'} {/* No notes */}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Partial Delivery Dialog */}
      <Dialog 
        open={partialDeliveryOpen} 
        onClose={() => setPartialDeliveryOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Teillieferung erfassen {/* Record Partial Delivery */}
          {selectedPosition && (
            <Typography variant="subtitle2" color="text.secondary">
              Position #{selectedPosition.position_number} - {selectedPosition.part_name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              {selectedPosition && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Verfügbar zur Lieferung: {selectedPosition.open_quantity} Einheiten {/* Available to deliver: */}
                </Alert>
              )}
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Zu liefernde Menge" /* Quantity to Deliver */
                type="number"
                fullWidth
                value={deliveryForm.delivered_quantity}
                onChange={(e) => setDeliveryForm(prev => ({
                  ...prev,
                  delivered_quantity: e.target.value
                }))}
                inputProps={{ 
                  min: 1, 
                  max: selectedPosition?.open_quantity || 1 
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Lieferdatum" /* Delivery Date */
                type="date"
                fullWidth
                value={deliveryForm.delivery_date}
                onChange={(e) => setDeliveryForm(prev => ({
                  ...prev,
                  delivery_date: e.target.value
                }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Empfangen von" /* Received By */
                fullWidth
                value={deliveryForm.received_by}
                onChange={(e) => setDeliveryForm(prev => ({
                  ...prev,
                  received_by: e.target.value
                }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Lieferanmerkungen" /* Delivery Notes */
                fullWidth
                multiline
                rows={3}
                value={deliveryForm.delivery_notes}
                onChange={(e) => setDeliveryForm(prev => ({
                  ...prev,
                  delivery_notes: e.target.value
                }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPartialDeliveryOpen(false)}>
            Abbrechen {/* Cancel */}
          </Button>
          <Button
            onClick={handlePartialDelivery}
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Erfassung läuft...' : 'Lieferung erfassen'} {/* Recording... | Record Delivery */}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Delivery Dialog */}
      <Dialog 
        open={bulkDeliveryOpen} 
        onClose={() => setBulkDeliveryOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Alle Lieferungen abschließen {/* Complete All Deliveries */}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Dies markiert ALLE verbleibenden Positionen als vollständig geliefert. {/* This will mark ALL remaining positions as fully delivered. */}
              </Alert>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Lieferdatum" /* Delivery Date */
                type="date"
                fullWidth
                value={deliveryForm.delivery_date}
                onChange={(e) => setDeliveryForm(prev => ({
                  ...prev,
                  delivery_date: e.target.value
                }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Empfangen von" /* Received By */
                fullWidth
                value={deliveryForm.received_by}
                onChange={(e) => setDeliveryForm(prev => ({
                  ...prev,
                  received_by: e.target.value
                }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Lieferanmerkungen" /* Delivery Notes */
                fullWidth
                multiline
                rows={3}
                value={deliveryForm.delivery_notes}
                onChange={(e) => setDeliveryForm(prev => ({
                  ...prev,
                  delivery_notes: e.target.value
                }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeliveryOpen(false)}>
            Abbrechen {/* Cancel */}
          </Button>
          <Button
            onClick={handleBulkDelivery}
            variant="contained"
            disabled={loading}
            color="primary"
          >
            {loading ? 'Verarbeitung läuft...' : 'Alle Lieferungen abschließen'} {/* Processing... | Complete All Deliveries */}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
